import React, { useState, useEffect } from "react";
import {
  onSubmit,
  calculatePartNumber,
  calculateVersionNumber,
} from "./CostPlanningUpload.functions";
import { tranStr, translate } from "../../utils/translation";
import "../dropzone/Dropzone.css";

import "./CostPlanningUpload.css";
import isEmpty from "../../validation/is-empty";
import { useCbs, useCbsCodes } from "../../api/cbs/CbsAPI";
import Spinner from "../common/Spinner";
import PropTypes from "prop-types";
import DragDropUploadFile from "../common/generic-upload-button/DragDropUploadFile";

export default function CostPlanningUpload(props) {
  const { projectID } = props;
  const { project } = props;
  const { CPs } = props;
  const { CP } = props;

  const { setShow } = props;

  // List of CBS
  const isNotPublished = false;
  const { cbsCodes } = useCbsCodes(isNotPublished);
  // Selected CBS
  const [selectedCbs, setSelectedCbs] = useState("");
  const { cbs } = useCbs(selectedCbs);

  const [file, setFile] = useState([]);
  const [filePreview, setFilePreview] = useState([]);
  const [messages, setMessages] = useState([]);
  const [highlight, setHighlight] = useState(false);
  const [date, setDate] = useState(
    new Date().getFullYear() +
      "-" +
      (new Date().getMonth() + 1) +
      "-" +
      new Date().getDate()
  );
  const [stage, setStage] = useState(1);
  const [revision, setRevision] = useState(1);
  const [part, setPart] = useState(0);
  const [error, setError] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  // To reset File Input
  const [inputKey, setInputKey] = useState(0);

  // Establish Stage Number
  useEffect(() => {
    // If a selectedStage is available use it
    if (!isEmpty(CP)) {
      setStage(CP.stage);
    } else if (!isEmpty(CPs.data)) {
      // Otherwise use the first available stage
      setStage(CPs.data[0].stage);
    }
  }, [CPs.data, CP]);

  // Establish Starting Part Number
  useEffect(() => {
    if (!isEmpty(CPs.data)) {
      setPart(calculatePartNumber(CPs, stage, revision));
    }
  }, [CPs, stage, revision]);

  // Establish Starting Version Number
  useEffect(() => {
    if (!isEmpty(CPs.data)) {
      setRevision(calculateVersionNumber(CPs, stage));
    }
  }, [CPs, stage]);

  // Find the CBS Codes
  const categoryGroup = cbs?.data?.categoryGroups?.find(
    (g) => g.categoryName === "Key Categories"
  );

  const designContingencyItem = categoryGroup?.categoryItems?.find(
    (g) => g.description === "Design Contingency"
  );
  const taxItem = categoryGroup?.categoryItems?.find(
    (g) => g.description === "Tax"
  );

  let designContingencyCode = [];
  let taxCode = [];

  if (!isEmpty(designContingencyItem)) {
    designContingencyCode = designContingencyItem.codes;
  }

  if (!isEmpty(taxItem)) {
    taxCode = taxItem.codes;
  }

  let data = {
    projectID: projectID,
    project: project,
    stage: stage,
    revision: revision,
    date: date,
    part: part,
    standard: selectedCbs,
    file: file,
    CPs: CPs,
    setShow: setShow,
    designContingencyCode: designContingencyCode,
    taxCode: taxCode,
    setError: setError,
    setFile: setFile,
    setMessages: setMessages,
    setFilePreview: setFilePreview,
    setIsProcessing: setIsProcessing,
  };

  // Add a random query value to prevent caching
  const randomQueryValue = "?" + Date.now();

  return (
    <div className="cost-planning-upload-container">
      <div className="cost-planning-upload">
        <div className="cost-planning-upload-control-container">
          <DragDropUploadFile
            inputKey={inputKey}
            setInputKey={setInputKey}
            setFile={setFile}
            setFilePreview={setFilePreview}
            setHighlight={setHighlight}
            highlight={highlight}
            setError={setError}
            setMessages={setMessages}
            allowMultiple={true}
          />
          <div className="cost-planning-upload-control">
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Cost Planning Guide")}:
              </div>
              <a
                className="cost-planning-upload-document-button"
                href={
                  "https://stcostclarityprd001.blob.core.windows.net/1-guides/Cost Planning Guide.pdf" +
                  randomQueryValue
                }
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                {translate("Download")}
              </a>
            </div>
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Codes, Sectors List (NRM)")}:
              </div>
              <a
                className="cost-planning-upload-document-button"
                href={
                  "https://stcostclarityprd001.blob.core.windows.net/1-guides/Codes and Sector Lists (NRM).pdf" +
                  randomQueryValue
                }
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                {translate("Download")}
              </a>
            </div>
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Codes, Sectors List (ICMS)")}:
              </div>
              <a
                className="cost-planning-upload-document-button"
                href={
                  "https://stcostclarityprd001.blob.core.windows.net/1-guides/Codes and Sector Lists (ICMS).pdf" +
                  randomQueryValue
                }
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                {translate("Download")}
              </a>
            </div>
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Cost Planning Template")}:
              </div>
              <a
                className="cost-planning-upload-document-button"
                href={
                  "https://stcostclarityprd001.blob.core.windows.net/1-guides/Cost Estimate Template.xlsx" +
                  randomQueryValue
                }
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                {translate("Download")}
              </a>
            </div>
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Date (YYYY-MM-DD)")}:
              </div>
              <input
                className="cost-planning-upload-control-input"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                }}
              />
            </div>
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Upload to Stage")}:
              </div>
              <select
                value={stage}
                className="cost-planning-upload-control-input"
                onChange={(e) => {
                  setStage(e.target.value);
                }}
              >
                {selectStages(CPs.data)}
              </select>
            </div>
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Upload to Release")}:
              </div>
              <select
                value={revision}
                className="cost-planning-upload-control-input"
                onChange={(e) => {
                  setRevision(e.target.value);
                }}
              >
                {selectVersions(CPs, stage)}
              </select>
            </div>
            <div className="cost-planning-upload-control-row">
              <div className="cost-planning-upload-control-label">
                {translate("Cost Breakdown Structure")}:
              </div>
              <select
                value={selectedCbs}
                className="cost-planning-upload-control-input"
                onChange={(e) => {
                  setSelectedCbs(e.target.value);
                }}
              >
                <option key="select-cbs" value="">
                  {"Select CBS"}
                </option>
                {selectOptions(
                  cbsCodes?.data?.map((c) => {
                    return { value: c, label: c };
                  })
                )}
              </select>
            </div>
          </div>
        </div>

        <div className="tpi-upload-button-container">
          <FileTray
            files={filePreview}
            messages={messages}
            part={part}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
          />
        </div>
        <div className="general-row-container">
          <DisplayError error={error} setError={setError} />
        </div>
        <div className="general-button-container">
          <button
            className="general-upload-button"
            onClick={() => {
              setIsProcessing(filePreview.length > 0);
              onSubmit(data);
            }}
          >
            {translate("Upload")}
          </button>
        </div>
      </div>
      <div className="cost-planning-upload-cbs-control-container">
        <div className="cost-planning-upload-cbs-control">
          <div className="general-row-container">
            <div className="cost-planning-upload-cbs-note">
              {tranStr(
                "The below codes have been detected in the selected CBS for identifying key values in your estimate. If these key values relate to your estimate, please ensure these codes have been used in your elemental coding exactly as shown below, otherwise features in Cost Clarity that depend on them will not work"
              )}
              :
            </div>
          </div>
          <DisplayRequiredCodes selectedCostType="On Costs" cbs={cbs} />
          <DisplayRequiredCodes selectedCostType="Key Categories" cbs={cbs} />
        </div>
      </div>
    </div>
  );
}

function DisplayRequiredCodes(props) {
  const { selectedCostType } = props;
  const { cbs } = props;

  const categoryGroup = cbs?.data?.categoryGroups?.find(
    (g) => g.categoryName === selectedCostType
  );

  const display = [];

  categoryGroup?.categoryItems?.forEach((categoryItem) => {
    display.push(
      <div
        key={categoryItem.id}
        className="cost-planning-upload-cbs-control-row"
      >
        <div className="cost-planning-upload-cbs-control-label">
          {translate(categoryItem.description)}:
        </div>
        <div className="cost-planning-upload-cbs-control-label">
          {displayCodes(categoryItem.codes)}
        </div>
      </div>
    );
  });

  return display;
}

function displayCodes(codes) {
  if (isEmpty(codes)) {
    return null;
  }

  const display = [];
  codes?.forEach((code, i) => {
    if (i < codes.length - 1) {
      display.push(code + ", ");
    } else {
      display.push(code);
    }
  });

  return display;
}

function DisplayError(props) {
  const { error } = props;
  return (
    <div className="feedback-error-container">
      <div className={error.type}>
        <b>{error.text}</b>
      </div>
    </div>
  );
}

/// props validation | SQ(javascript:S6774)
FileTray.propTypes = {
  files: PropTypes.array,
  messages: PropTypes.array,
  part: PropTypes.number,
  isProcessing: PropTypes.bool,
  setIsProcessing: PropTypes.func,
};
///
export function FileTray(props) {
  const { files } = props;
  const { messages } = props;
  const { part } = props;
  const { isProcessing, setIsProcessing } = props;

  messages.length > 0 && setIsProcessing(false);
  let display = [];
  for (let i = 0; i < files.length; i++) {
    display.push(
      <div>
        <div className="cost-planning-files-item" key={i}>
          {isProcessing && (
            <Spinner width="20px" height="20px" margin-right="20px" />
          )}
          <b>
            <div className="cost-planning-files-part">
              {tranStr("Part") + ` ${part + (i + 1)}`}
            </div>
          </b>
          <div className="cost-planning-files-item-name">{files[i].name}</div>
        </div>
        <div className="cost-planning-message">
          <Messages messages={messages} i={i} />
        </div>
      </div>
    );
  }

  return <div className="cost-planning-upload-files">{display}</div>;
}
/// props validation | SQ(javascript:S6774)
Messages.propTypes = {
  messages: PropTypes.string,
  i: PropTypes.number,
};
///
export function Messages(props) {
  const { messages, i } = props;
  const display = [];

  if (!isEmpty(messages)) {
    if (!isEmpty(messages[i])) {
      messages[i].response.message.forEach((element) => {
        display.push(
          <div
            className={
              messages[i].status === "fulfilled"
                ? "feedback-success"
                : "feedback-error"
            }
          >
            <li>{element}</li>
          </div>
        );
      });
    }
  }
  return display;
}

function selectStages(array) {
  return array.map(function (item, i) {
    if (item.stage_name !== "") {
      return (
        <option key={i} value={item.stage}>
          {item.stage_name}
        </option>
      );
    } else {
      return null;
    }
  });
}

function selectVersions(CPs, stage) {
  // Unique list of revisions
  const revisions = new Set();

  // Within this stage
  CPs.data.forEach((CPStage) => {
    CPStage.versions.forEach((CP) => {
      if (parseInt(CP.stage) === parseInt(stage)) {
        // Extract all the version numbers
        CP.estimates.forEach((estimate) => {
          revisions.add(estimate.revision);
        });
      }
    });
  });

  // Format the revisions into the drop down list
  const revisionDropDownList = [];

  revisions.forEach((r) => {
    revisionDropDownList.push({
      revision: r,
      revision_name: tranStr("Release") + " " + r,
    });
  });

  // Add latest
  if (!isEmpty(revisionDropDownList)) {
    const latestRevision = revisionDropDownList[0];
    revisionDropDownList.unshift({
      revision: latestRevision.revision + 1,
      revision_name:
        tranStr("Release") +
        " " +
        (latestRevision.revision + 1) +
        " " +
        tranStr("(New)"),
    });
  }

  // Add the default starting revision
  if (isEmpty(revisionDropDownList)) {
    revisionDropDownList.push({
      revision: 1,
      revision_name: tranStr("Release") + " " + 1 + " " + tranStr("(New)"),
    });
  }

  return revisionDropDownList.map(function (item, i) {
    if (item.revision_name !== "") {
      return (
        <option key={i} value={item.revision}>
          {item.revision_name}
        </option>
      );
    } else {
      return null;
    }
  });
}

function selectOptions(array) {
  return array?.map((item, i) => {
    return (
      <option key={i} value={item.value}>
        {item.label}
      </option>
    );
  });
}
